import { z } from "zod";

import { emailSchema } from "./auth";
import { RECORD_ACCESS_LEVELS } from "./recordAccess";

/**
 * HARİCÎ SÖZLEŞME KATILIMCISI daveti şemaları (CLOS-145 `[126]`).
 *
 * Davet, organizasyon üyesi olmayan gerçek kişiyi TEK sözleşmeye, açık erişim seviyesine
 * ve süreye bağlar. Gönderim erişim AÇMAZ: kabul (CLOS-146) gerçekleşene kadar yalnız
 * bekleyen bir kayıt vardır; kayıt erişimi kabulde CLOS-91 sözleşmesiyle doğar.
 */

/**
 * Sürümlü konfigürasyon (kartın AÇIK KARARI): davet süresi, yeniden gönderim aralığı ve
 * günlük limit `config/externalParticipant.json`'da yaşar — kod dalı değil.
 */
export const externalParticipantConfigSchema = z.object({
  version: z.number().int().min(1),
  inviteExpiryDays: z.number().int().min(1),
  inviteMaxPerDay: z.number().int().min(1),
  resendIntervalMinutes: z.number().int().min(1),
  /**
   * Haricî erişimin TEK yenilemede uzatılabileceği azami gün (CLOS-148 `[129]` açık
   * kararı). Üyeliksiz bir kişinin erişimi süresizleştirilemez ve bu tavanı aşamaz;
   * iç üyelik erişimleri bu sınırın DIŞINDA (davranışları değişmedi).
   */
  renewalMaxDays: z.number().int().min(1),
  /** Süre sonu YAKLAŞIRKEN uyarı günleri — bildirim tüketicisi kendi kartıyla gelecek. */
  expiryWarningDays: z.array(z.number().int().min(1)),
  /** Süre sonu worker'ının koşu aralığı (dakika) — cron ifadesi bundan üretilir. */
  expiryWorkerIntervalMinutes: z.number().int().min(1).max(59),
});
export type ExternalParticipantConfig = z.infer<typeof externalParticipantConfigSchema>;

/**
 * GUEST YALNIZ GÖRÜNTÜLEME (CLOS-1127 · rapor S.6/S.7 · Halit K5/K6).
 *
 * `accessLevel` artık SEÇİLMEZ: yalnız `"read"` kabul edilir (eski istemcilerle uyum için
 * gönderilebilir; `"write"` 422). Seviye sunucuda sabit. İKİ AYRI TARİH (Halit K6):
 * davetin `expiresAt`'i yalnız KABUL son tarihi (konfigden); `accessEndsAt` kabul SONRASI
 * erişimin biteceği an — opsiyonel, yoksa erişim kaldırılana kadar sürer. Yeniden gönderim
 * kabul penceresini yeniler, `accessEndsAt`'e dokunmaz.
 */
export const createParticipantInvitationSchema = z.strictObject({
  // `emailSchema`: davet ile kabul aynı normalizasyondan geçmeli (`inviteMemberSchema`
  // ile aynı gerekçe) — büyük/küçük harf farkı eşleşmeyi bozmamalı.
  email: emailSchema,
  accessLevel: z.literal("read", "Haricî misafir yalnız görüntüleme erişimi alabilir").optional(),
  accessEndsAt: z.iso.datetime({ message: "Geçerli bir bitiş zamanı giriniz" }).nullish(),
});

/** Kabul sonrası misafir erişiminin durumu — davet durumundan AYRI eksen (Halit). */
export const GUEST_ACCESS_STATES = ["none", "active", "expired", "revoked"] as const;
export type GuestAccessState = (typeof GUEST_ACCESS_STATES)[number];
export const GUEST_ACCESS_STATE_NAMES: Record<GuestAccessState, string> = {
  none: "Erişim yok",
  active: "Aktif",
  expired: "Süresi doldu",
  revoked: "Kaldırıldı",
};
export type CreateParticipantInvitationInput = z.infer<typeof createParticipantInvitationSchema>;

export type ParticipantInvitationStatus =
  "pending" | "accepted" | "rejected" | "cancelled" | "expired";

export const participantInvitationTokenSchema = z.strictObject({
  token: z.string().min(1, "Davet bağlantısı geçersiz"),
});

/**
 * Token'la görüntüleme (CLOS-146 `[127]`) — oturumsuz uç, bilerek DAR ama sözleşme
 * BAŞLIĞINI taşıyor: davet e-postası başlığı bilerek taşımıyordu ("kapısı olan yerden
 * öğrenir") ve o kapı burası — token'ı olan kişi neye çağrıldığını görmeden karar
 * verememeli. E-posta MASKELİ; erişim kararı kabul anındaki tam-adres eşleşmesine dayanır.
 */
export type ParticipantInvitationPublicView = {
  id: string;
  organizationName: string;
  contractTitle: string;
  email: string;
  accessLevel: (typeof RECORD_ACCESS_LEVELS)[number];
  expiresAt: string;
  /** Kabul sonrası erişim bitişi (K6); `null` = kaldırılana kadar. */
  accessEndsAt: string | null;
  status: ParticipantInvitationStatus;
};

/** Davet gönderen tarafın liste satırı — token TAŞIMAZ (token yalnız davet e-postasında). */
export type ParticipantInvitationSummary = {
  id: string;
  contractId: string;
  email: string;
  accessLevel: (typeof RECORD_ACCESS_LEVELS)[number];
  status: ParticipantInvitationStatus;
  expiresAt: string;
  /** Kabul sonrası erişim bitişi (K6); `null` = kaldırılana kadar. */
  accessEndsAt: string | null;
  /** Erişim ekseni: kabul edilmemişse `none`; kabul edildiyse satırın durumu. */
  accessState: GuestAccessState;
  /** Kabulde açılan `record_access` satırı (kaldırma/süre için); yoksa `null`. */
  accessId: string | null;
  invitedByName: string;
  createdAt: string;
};
